import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Shield, Users, Clock, ChevronRight, Menu, X } from 'lucide-react';
import { heroContent } from '../data/content';
import { trackCTAClick } from '../lib/ga4';

const iconMap = {
  Shield: Shield,
  Users: Users,
  Clock: Clock,
};

const Hero = () => {
  const handleCTAClick = (ctaName) => {
    trackCTAClick(ctaName, 'hero');
  };

  return (
    <section
      id="hero"
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/30f17ead-ad5a-4309-a721-a4571fbca3b8/images/2d05673e0858e49712b649f8e4697c7b581344ed763e73a036e89cf458953b8c.png"
          alt="Équipe stratégique en réunion sur terrasse ensoleillée"
          className="w-full h-full object-cover object-center"
        />
        {/* Elegant overlay gradient for text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(233, 223, 207, 0.95) 0%, rgba(233, 223, 207, 0.88) 45%, rgba(233, 223, 207, 0.65) 75%, rgba(233, 223, 207, 0.45) 100%)'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Content - Left aligned as per design guidelines */}
          <div className="lg:col-span-7 space-y-8">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium animate-fade-in"
              style={{ backgroundColor: '#D8C9B3', color: '#2C2A27' }}
            >
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#2C2A27' }} />
              {heroContent.badge}
            </div>

            {/* Title */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 animate-fade-in stagger-1"
              style={{ letterSpacing: '-0.04em' }}
            >
              {heroContent.title}
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl animate-fade-in stagger-2">
              {heroContent.subtitle}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 animate-fade-in stagger-3">
              <a
                href="#diagnostic"
                onClick={() => handleCTAClick('diagnostic_hero')}
                data-testid="hero-cta-diagnostic"
              >
                <Button
                  size="lg"
                  className="text-white font-semibold py-4 px-8 rounded-full w-full sm:w-auto transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                  style={{ 
                    backgroundColor: '#1C2E4A',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#0F1E33';
                    e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1C2E4A';
                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  Demander un diagnostic stratégique
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link
                to="/simulation-roi"
                onClick={() => handleCTAClick('simulation_roi_hero')}
                data-testid="hero-cta-simulation"
              >
                <Button
                  size="lg"
                  className="font-semibold py-4 px-8 rounded-full w-full sm:w-auto transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                  style={{ 
                    backgroundColor: '#D8C9B3',
                    color: '#1C2E4A',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#C9BAA4';
                    e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#D8C9B3';
                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  Simuler votre ROI IA
                </Button>
              </Link>
              <Link
                to="/contact"
                onClick={() => handleCTAClick('appel_hero')}
                data-testid="hero-cta-appel"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="border-gray-200 text-gray-900 font-medium py-4 px-8 rounded-full w-full sm:w-auto transition-all duration-300 ease-out hover:bg-gray-50 hover:border-gray-300 hover:-translate-y-0.5 active:scale-[0.98] active:bg-gray-100 active:translate-y-0"
                  style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}
                  onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.08)'}
                  onMouseLeave={(e) => e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'}
                >
                  Être rappelé
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 pt-4 animate-fade-in stagger-4">
              {heroContent.badges.map((badge, index) => {
                const IconComponent = iconMap[badge.icon];
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-gray-600"
                    data-testid={`hero-badge-${index}`}
                  >
                    {IconComponent && <IconComponent className="w-5 h-5 text-blue-700" />}
                    <span className="text-sm font-medium">{badge.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual - Right side */}
          <div className="lg:col-span-5 lg:pl-12 animate-fade-in stagger-5">
            <div className="relative">
              {/* Main card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Audit gratuit</p>
                      <p className="text-sm text-gray-500">Évaluez votre potentiel IA</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <span className="text-sm text-gray-700">Analyse de vos processus</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <span className="text-sm text-gray-700">Recommandations personnalisées</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <span className="text-sm text-gray-700">Score de maturité IA</span>
                    </div>
                  </div>

                  <a href="#diagnostic" className="block">
                    <Button
                      className="w-full text-white rounded-full py-3"
                      data-testid="hero-card-cta"
                      style={{ backgroundColor: '#1C2E4A' }}
                    >
                      Commencer le diagnostic
                    </Button>
                  </a>
                </div>
              </div>

              {/* Floating stat card */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100 hidden md:block">
                <p className="text-2xl font-bold text-blue-700">+40%</p>
                <p className="text-sm text-gray-500">Productivité moyenne</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
